/**
 * Team React Query Hooks
 *
 * Provides hooks for managing team members, invites, and permissions.
 * Handles member CRUD, invite lifecycle, and permission management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';

// =============================================================================
// TYPES (re-exported, now owned here)
// =============================================================================

export type TeamRole = 'owner' | 'admin' | 'member' | 'guest';
export type TeamMemberStatus = 'active' | 'inactive' | 'suspended';
export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'cancelled';
export type PermissionLevel = 'none' | 'view' | 'edit' | 'admin';

export interface ModulePermissions {
  home: PermissionLevel;
  projects: PermissionLevel;
  calendar: PermissionLevel;
  hub: PermissionLevel;
  accounting: PermissionLevel;
  settings: PermissionLevel;
}

export interface TeamMember {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  phone?: string;
  role: TeamRole;
  status: TeamMemberStatus;
  permissions: ModulePermissions;
  joinedAt?: string;
  lastAccessAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TeamInvite {
  id: string;
  email: string;
  role: TeamRole;
  permissions: ModulePermissions;
  status: InviteStatus;
  token: string;
  invitedBy: string;
  message?: string;
  expiresAt: string;
  sentAt?: string;
  acceptedAt?: string;
  createdAt: string;
}

// =============================================================================
// DEFAULTS
// =============================================================================

export const DEFAULT_PERMISSIONS: ModulePermissions = {
  home: 'view',
  projects: 'view',
  calendar: 'view',
  hub: 'view',
  accounting: 'none',
  settings: 'none',
};

// =============================================================================
// GRAPHQL
// =============================================================================

const TEAM_MEMBERS_QUERY = `
  query GetTeamMembers($status: String) {
    teamMembers(status: $status) {
      id
      email
      displayName
      avatarUrl
      phone
      role
      status
      permissions {
        home
        projects
        calendar
        hub
        accounting
        settings
      }
      joinedAt
      lastAccessAt
      createdAt
      updatedAt
    }
  }
`;

const TEAM_INVITES_QUERY = `
  query GetTeamInvites($status: String) {
    teamInvites(status: $status) {
      id
      email
      role
      permissions {
        home
        projects
        calendar
        hub
        accounting
        settings
      }
      status
      token
      invitedBy
      message
      expiresAt
      sentAt
      acceptedAt
      createdAt
    }
  }
`;

const CREATE_TEAM_MEMBER_MUTATION = `
  mutation CreateTeamMember($input: TeamMemberInput!) {
    createTeamMember(input: $input) {
      id
      email
      displayName
      role
      status
      permissions {
        home
        projects
        calendar
        hub
        accounting
        settings
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_MEMBER_MUTATION = `
  mutation UpdateTeamMember($id: ID!, $input: TeamMemberInput!) {
    updateTeamMember(id: $id, input: $input) {
      id
      email
      displayName
      role
      status
      permissions {
        home
        projects
        calendar
        hub
        accounting
        settings
      }
      updatedAt
    }
  }
`;

const DELETE_TEAM_MEMBER_MUTATION = `
  mutation DeleteTeamMember($id: ID!) {
    deleteTeamMember(id: $id)
  }
`;

const CREATE_TEAM_INVITE_MUTATION = `
  mutation CreateTeamInvite($input: TeamInviteInput!) {
    createTeamInvite(input: $input) {
      id
      email
      role
      permissions {
        home
        projects
        calendar
        hub
        accounting
        settings
      }
      status
      token
      invitedBy
      message
      expiresAt
      createdAt
    }
  }
`;

const CANCEL_TEAM_INVITE_MUTATION = `
  mutation CancelTeamInvite($id: ID!) {
    cancelTeamInvite(id: $id)
  }
`;

const RESEND_TEAM_INVITE_MUTATION = `
  mutation ResendTeamInvite($id: ID!) {
    resendTeamInvite(id: $id) {
      id
      sentAt
    }
  }
`;

const ACCEPT_TEAM_INVITE_MUTATION = `
  mutation AcceptTeamInvite($token: String!) {
    acceptTeamInvite(token: $token) {
      id
      email
      displayName
      role
      permissions {
        home
        projects
        calendar
        hub
        accounting
        settings
      }
      createdAt
    }
  }
`;

// =============================================================================
// MAPPERS
// =============================================================================

/**
 * Map GraphQL permissions to local type
 */
function mapGraphQLPermissions(perms: any): ModulePermissions {
  return {
    home: (perms.home as PermissionLevel) || 'none',
    projects: (perms.projects as PermissionLevel) || 'none',
    calendar: (perms.calendar as PermissionLevel) || 'none',
    hub: (perms.hub as PermissionLevel) || 'none',
    accounting: (perms.accounting as PermissionLevel) || 'none',
    settings: (perms.settings as PermissionLevel) || 'none',
  };
}

/**
 * Map local permissions to GraphQL input
 */
function permissionsToInput(perms: ModulePermissions) {
  return {
    home: perms.home,
    projects: perms.projects,
    calendar: perms.calendar,
    hub: perms.hub,
    accounting: perms.accounting,
    settings: perms.settings,
  };
}

/**
 * Map GraphQL member to local type
 */
function mapGraphQLMember(data: any): TeamMember {
  return {
    id: data.id,
    email: data.email,
    displayName: data.displayName,
    avatarUrl: data.avatarUrl || undefined,
    phone: data.phone || undefined,
    role: data.role as TeamRole,
    status: data.status as TeamMemberStatus,
    permissions: mapGraphQLPermissions(data.permissions),
    joinedAt: data.joinedAt || undefined,
    lastAccessAt: data.lastAccessAt || undefined,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

/**
 * Map GraphQL invite to local type
 */
function mapGraphQLInvite(data: any): TeamInvite {
  return {
    id: data.id,
    email: data.email,
    role: data.role as TeamRole,
    permissions: mapGraphQLPermissions(data.permissions),
    status: data.status as InviteStatus,
    token: data.token,
    invitedBy: data.invitedBy,
    message: data.message || undefined,
    expiresAt: data.expiresAt,
    sentAt: data.sentAt || undefined,
    acceptedAt: data.acceptedAt || undefined,
    createdAt: data.createdAt,
  };
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch team members from the server.
 * Optionally filter by status.
 */
export function useTeamMembers(status?: TeamMemberStatus) {
  return useQuery({
    queryKey: [...queryKeys.team.members(), status].filter(Boolean),
    queryFn: async () => {
      const response = await graphqlFetch<{ teamMembers: any[] }>(TEAM_MEMBERS_QUERY, {
        status: status || null,
      });
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.teamMembers || []).map(mapGraphQLMember);
    },
    staleTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Fetch team invites from the server.
 * Optionally filter by status.
 */
export function useTeamInvites(status?: InviteStatus) {
  return useQuery({
    queryKey: [...queryKeys.team.invites(), status].filter(Boolean),
    queryFn: async () => {
      const response = await graphqlFetch<{ teamInvites: any[] }>(TEAM_INVITES_QUERY, {
        status: status || null,
      });
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.teamInvites || []).map(mapGraphQLInvite);
    },
    staleTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Create a new team member.
 */
export function useCreateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (member: {
      email: string;
      displayName: string;
      role: TeamRole;
      permissions?: ModulePermissions;
      avatarUrl?: string;
      phone?: string;
    }) => {
      const input = {
        email: member.email,
        display_name: member.displayName,
        role: member.role,
        permissions: permissionsToInput(member.permissions || DEFAULT_PERMISSIONS),
        avatar_url: member.avatarUrl || null,
        phone: member.phone || null,
      };
      const response = await graphqlFetch<{ createTeamMember: any }>(
        CREATE_TEAM_MEMBER_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.createTeamMember) {
        return mapGraphQLMember(response.data.createTeamMember);
      }
      throw new Error('Failed to create team member');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.team.members() });
    },
  });
}

/**
 * Update a team member's details, role, permissions, or status.
 */
export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Omit<TeamMember, 'id' | 'email' | 'createdAt' | 'updatedAt'>>;
    }) => {
      const input = {
        display_name: updates.displayName || null,
        role: updates.role || null,
        status: updates.status || null,
        permissions: updates.permissions ? permissionsToInput(updates.permissions) : null,
        avatar_url: updates.avatarUrl || null,
        phone: updates.phone || null,
      };
      const response = await graphqlFetch<{ updateTeamMember: any }>(
        UPDATE_TEAM_MEMBER_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.updateTeamMember) {
        return mapGraphQLMember(response.data.updateTeamMember);
      }
      throw new Error('Failed to update team member');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.team.members() });
    },
  });
}

/**
 * Delete a team member.
 */
export function useDeleteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteTeamMember: boolean }>(
        DELETE_TEAM_MEMBER_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.deleteTeamMember) {
        throw new Error('Failed to delete team member');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.team.members() });
    },
  });
}

/**
 * Create a new team invite.
 */
export function useCreateTeamInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invite: {
      email: string;
      role: TeamRole;
      permissions?: ModulePermissions;
      message?: string;
    }) => {
      const input = {
        email: invite.email,
        role: invite.role,
        permissions: permissionsToInput(invite.permissions || DEFAULT_PERMISSIONS),
        message: invite.message || null,
      };
      const response = await graphqlFetch<{ createTeamInvite: any }>(
        CREATE_TEAM_INVITE_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.createTeamInvite) {
        return mapGraphQLInvite(response.data.createTeamInvite);
      }
      throw new Error('Failed to create team invite');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.team.invites() });
    },
  });
}

/**
 * Cancel a team invite.
 */
export function useCancelTeamInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ cancelTeamInvite: boolean }>(
        CANCEL_TEAM_INVITE_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.cancelTeamInvite) {
        throw new Error('Failed to cancel team invite');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.team.invites() });
    },
  });
}

/**
 * Resend a team invite email.
 */
export function useResendTeamInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ resendTeamInvite: any }>(
        RESEND_TEAM_INVITE_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.resendTeamInvite) {
        throw new Error('Failed to resend team invite');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.team.invites() });
    },
  });
}

/**
 * Accept a team invite (by token).
 */
export function useAcceptTeamInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await graphqlFetch<{ acceptTeamInvite: any }>(
        ACCEPT_TEAM_INVITE_MUTATION,
        { token }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.acceptTeamInvite) {
        return mapGraphQLMember(response.data.acceptTeamInvite);
      }
      throw new Error('Failed to accept team invite');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.team.members() });
      qc.invalidateQueries({ queryKey: queryKeys.team.invites() });
    },
  });
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Filter active members from a member list.
 */
export function getActiveMembers(members: TeamMember[]): TeamMember[] {
  return members.filter((m) => m.status === 'active');
}

/**
 * Filter pending invites from an invite list.
 */
export function getPendingInvites(invites: TeamInvite[]): TeamInvite[] {
  return invites.filter((i) => i.status === 'pending');
}
